/**
 * ManageColumnsDialog Component
 *
 * A shared dialog that allows users to toggle the visibility of columns
 * in any table. Changes are staged locally and only applied when the user
 * clicks "Save". Clicking "Cancel" discards any changes.
 *
 * Generic over the column key type so it can be used with any entity
 * (e.g. JournalArticle, Survey).
 */

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"

/**
 * Describes a group of one or more columns that are toggled together.
 *
 * @template K - The union type of valid column keys.
 */
export interface ColumnGroup<K extends string> {
  /** The column keys belonging to this group. */
  keys: K[]
  /** Human-readable label displayed in the dialog. */
  label: string
}

/**
 * Props for the ManageColumnsDialog component.
 *
 * @template K - The union type of valid column keys.
 */
interface ManageColumnsDialogProps<K extends string> {
  /** Whether the dialog is currently open. */
  open: boolean
  /** Callback to control open state. */
  onOpenChange: (open: boolean) => void
  /** Column groups available for toggling. */
  columnGroups: ColumnGroup<K>[]
  /** Set of currently visible column keys (source of truth from parent). */
  visibleKeys: Set<K>
  /** Set of columns that the user should not be able to toggle off. Optional. */
  fixedColumns?: Set<K>
  /** Callback fired with the new visible keys when the user clicks Save. */
  onSave: (keys: Set<K>) => void
}

/**
 * Dialog for managing which columns are visible in a table.
 *
 * Maintains a local draft of visible keys. On open, the draft is synced
 * to the parent's current state. The user can toggle column groups, select
 * all, or deselect all within the draft. Clicking "Save" commits the draft
 * to the parent and closes the dialog. Clicking "Cancel" discards changes.
 *
 * @template K - The union type of valid column keys.
 * @param props - Component props.
 * @param props.open - Whether the dialog is open.
 * @param props.onOpenChange - Callback to control open state.
 * @param props.columnGroups - Array of column groups to display.
 * @param props.visibleKeys - Current visible keys from the parent.
 * @param props.fixedColumns - Columns that can't be toggled off. 
 * @param props.onSave - Callback with the final set of keys on save.
 */
export default function ManageColumnsDialog<K extends string>({
  open,
  onOpenChange,
  columnGroups,
  visibleKeys,
  fixedColumns,
  onSave,
}: ManageColumnsDialogProps<K>): React.JSX.Element {
  // Local draft state — reset from parent whenever the dialog opens
  const [draftKeys, setDraftKeys] = useState<Set<K>>(new Set(visibleKeys))

  // Sync draft to parent state each time the dialog opens
  useEffect(() => {
    if (open) {
      setDraftKeys(new Set(visibleKeys))
    }
  }, [open, visibleKeys])

  /**
   * Toggle all keys in a column group within the draft.
   *
   * @param keys - The column keys to toggle.
   */
  const handleToggleGroup = (keys: K[]): void => {
    setDraftKeys((prev) => {
      const next = new Set(prev)
      const allVisible = keys.every((k) => next.has(k))
      keys.forEach((k) => (allVisible ? next.delete(k) : next.add(k)))
      return next
    })
  }

  /**
   * Select all column keys in the draft.
   */
  const handleSelectAll = (): void => {
    const allKeys = new Set<K>()
    columnGroups.forEach((group) => group.keys.forEach((k) => allKeys.add(k)))
    setDraftKeys(allKeys)
  }

  /**
   * Deselect all column keys in the draft, preserving fixed columns.
   */
  const handleDeselectAll = (): void => {
    setDraftKeys(new Set(fixedColumns ?? []))
  }

  /**
   * Commit the draft and close the dialog.
   */
  const handleSave = (): void => {
    onSave(draftKeys)
    onOpenChange(false)
  }

  /**
   * Discard changes and close the dialog.
   */
  const handleCancel = (): void => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage columns</DialogTitle>
          <DialogDescription>
            Choose which columns are visible in the table.
          </DialogDescription>
        </DialogHeader>

        {/* Bulk actions */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleSelectAll}>
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
            Deselect all
          </Button>
        </div>

        {/* Scrollable column group list */}
        <div className="max-h-[400px] min-h-[380px] overflow-y-auto overflow-x-hidden">
          <div className="flex flex-col gap-3">
            {columnGroups.map((group) => {
              let toggleable = true
              if (fixedColumns !== undefined) {
                toggleable = !group.keys.some((k) => fixedColumns.has(k))
              }
              const checked = group.keys.every((k) => draftKeys.has(k))
              return (
                <label
                  key={group.keys[0]}
                  className="flex items-center justify-between gap-3 cursor-pointer"
                >
                  <span className="text-sm">{group.label}</span>
                  <Switch
                    checked={checked}
                    onCheckedChange={() => handleToggleGroup(group.keys)}
                    disabled={!toggleable}
                    size="sm"
                  />
                </label>
              )
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
